import {
  deleteApi,
  getApi,
  getApiById,
  postApi,
  postApiWithoutBody,
  putApi,
} from '../../Shared/Services/RestService';
import { PaginationResponse } from '../../Shared/Pagination/PaginationResponse';
import {
  EmailFolderCounts,
  EmailMessage,
  EmailMessagePayload,
  EmailPreview,
  EmailRecipient,
  EmailSenderProfile,
  EmailSenderProfilePayload,
  EmailTemplate,
  EmailTemplatePayload,
  EmailTemplateVariable,
} from '../Types/EmailAdminType';

const TEMPLATES = '/admin/email/templates';
const MESSAGES = '/admin/email/messages';
const RECIPIENTS = '/admin/email/recipients';
const SENDERS = '/admin/email/senders';

/* ==========================================================================
   Templates
   ========================================================================== */

export const getEmailTemplates = (params?: {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  return getApi<PaginationResponse<EmailTemplate>>(TEMPLATES, params);
};

export const getActiveEmailTemplates = () => {
  return getApi<EmailTemplate[]>(`${TEMPLATES}/active`);
};

export const getEmailTemplate = (id: string) => {
  return getApiById<EmailTemplate>(TEMPLATES, id);
};

export const getEmailTemplateVariables = () => {
  return getApi<EmailTemplateVariable[]>(`${TEMPLATES}/variables`);
};

export const createEmailTemplate = (payload: EmailTemplatePayload) => {
  return postApi<EmailTemplate>(TEMPLATES, payload);
};

export const updateEmailTemplate = (id: string, payload: EmailTemplatePayload) => {
  return putApi<EmailTemplate>(TEMPLATES, id, payload);
};

export const deleteEmailTemplate = (id: string) => {
  return deleteApi<null>(TEMPLATES, id);
};

export const previewEmailTemplate = (payload: {
  subject?: string;
  htmlBody?: string;
  sampleData?: Record<string, string>;
}) => {
  return postApi<EmailPreview>(`${TEMPLATES}/preview`, payload);
};

export const testSendEmailTemplate = (
  id: string,
  payload: { toEmail: string; subject?: string; htmlBody?: string; sampleData?: Record<string, string> }
) => {
  return postApi<EmailPreview>(`${TEMPLATES}/${id}/test-send`, payload);
};

/* ==========================================================================
   Messages (Email Trigger)
   ========================================================================== */

export const getEmailMessages = (params?: {
  folder?: string;
  search?: string;
  recipientType?: string;
  page?: number;
  size?: number;
}) => {
  return getApi<PaginationResponse<EmailMessage>>(MESSAGES, params);
};

export const getEmailMessageCounts = () => {
  return getApi<EmailFolderCounts>(`${MESSAGES}/counts`);
};

export const getEmailMessage = (id: string) => {
  return getApiById<EmailMessage>(MESSAGES, id);
};

export const createEmailDraft = (payload: EmailMessagePayload) => {
  return postApi<EmailMessage>(`${MESSAGES}/drafts`, payload);
};

export const updateEmailMessage = (id: string, payload: EmailMessagePayload) => {
  return putApi<EmailMessage>(MESSAGES, id, payload);
};

export const sendEmailDraft = (id: string) => {
  return postApiWithoutBody<EmailMessage>(`${MESSAGES}/${id}/send`);
};

export const sendEmailNow = (payload: EmailMessagePayload) => {
  return postApi<EmailMessage>(`${MESSAGES}/send`, payload);
};

export const starEmailMessage = (id: string, starred: boolean) => {
  return postApiWithoutBody<EmailMessage>(`${MESSAGES}/${id}/star?starred=${starred}`);
};

export const trashEmailMessage = (id: string) => {
  return postApiWithoutBody<EmailMessage>(`${MESSAGES}/${id}/trash`);
};

export const restoreEmailMessage = (id: string) => {
  return postApiWithoutBody<EmailMessage>(`${MESSAGES}/${id}/restore`);
};

export const deleteEmailMessage = (id: string) => {
  return deleteApi<null>(MESSAGES, id);
};

/* ==========================================================================
   Recipients
   ========================================================================== */

export const getEmailRecipients = (params?: {
  type?: string;
  status?: string;
  search?: string;
  page?: number;
  size?: number;
}) => {
  return getApi<PaginationResponse<EmailRecipient>>(RECIPIENTS, params);
};

/* ==========================================================================
   Sender profiles
   ========================================================================== */

export const getEmailSenderProfiles = (params?: {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  return getApi<PaginationResponse<EmailSenderProfile>>(SENDERS, params);
};

export const getActiveEmailSenderProfiles = () => {
  return getApi<EmailSenderProfile[]>(`${SENDERS}/active`);
};

export const getEmailSenderProfile = (id: string) => {
  return getApiById<EmailSenderProfile>(SENDERS, id);
};

export const createEmailSenderProfile = (payload: EmailSenderProfilePayload) => {
  return postApi<EmailSenderProfile>(SENDERS, payload);
};

export const updateEmailSenderProfile = (id: string, payload: EmailSenderProfilePayload) => {
  return putApi<EmailSenderProfile>(SENDERS, id, payload);
};

export const deleteEmailSenderProfile = (id: string) => {
  return deleteApi<null>(SENDERS, id);
};

export const testEmailSenderProfile = (payload: EmailSenderProfilePayload, toEmail: string) => {
  return postApi<null>(`${SENDERS}/test-send?toEmail=${encodeURIComponent(toEmail)}`, payload);
};
